import { useEffect, useRef } from "react";

/*
 Caselle che accettano soltanto cifre: il carattere sbagliato non viene rifiutato dopo, non
 viene proprio scritto.

 Perche' `beforeinput` e non `onKeyDown`. `onKeyDown` vede solo la tastiera: chi incolla, chi
 trascina un testo dentro il campo, chi detta, chi usa la sostituzione automatica del telefono
 non passa mai di li' e il carattere entra lo stesso. `beforeinput` e' l'unico evento che il
 browser emette **prima** di ogni modifica del valore, qualunque sia la via — battuta, incollata,
 trascinata, dettata — e che si puo' annullare con `preventDefault()`. Un `onChange` che ripulisce
 non basterebbe: su un `input type="number"` il browser consegna gia' `value === ""` quando il
 testo digitato non e' un numero, quindi il valore da ripulire non c'e' piu' e il campo resta
 vuoto invece che intatto.

 Non sostituisce niente. La validazione del profilo (`validazioniProfilo.ts`) e i vincoli del
 backend restano dove sono: questa e' la porta che si chiude prima, non la serratura.
*/

const SOLO_CIFRE = /^[0-9]*$/;

/*
 L'attributo che marca le caselle da sorvegliare. Si mette sull'`<input>`; l'ascolto vive su un
 antenato comune, perche' `beforeinput` risale il DOM e una pagina con cinquantadue caselle non
 puo' registrare cinquantadue ascoltatori.
*/
export const SOLO_CIFRE_ATTRIBUTO = "data-solo-cifre";

/*
 Il testo che l'evento sta per inserire, oppure `null` se non ne inserisce nessuno.

 Battitura e dettatura lo portano in `data`. Incolla e trascinamento lo lasciano invece nel
 `dataTransfer` e mettono `data` a `null`: leggere solo `data`, come fa quasi tutto il codice che
 si trova in giro, e' esattamente il buco che lascia passare l'incolla. Cancellazione, annulla e
 ripeti non inseriscono niente e vanno lasciati passare.
*/
export function testoInserito(evento: InputEvent): string | null {
  if (evento.data !== null && evento.data !== undefined) {
    return evento.data;
  }
  const trasferito = evento.dataTransfer?.getData("text");
  return trasferito !== undefined && trasferito !== "" ? trasferito : null;
}

/*
 Vero quando l'inserimento va fermato: contiene qualcosa che non e' una cifra. Il segno meno e la
 virgola cadono qui dentro, ed e' voluto — un conteggio di persone non e' negativo e non ha
 decimali, e una partita IVA e' fatta di sole cifre.
*/
export function inserimentoDaRifiutare(evento: InputEvent): boolean {
  const testo = testoInserito(evento);
  if (testo === null) {
    return false;
  }
  return !SOLO_CIFRE.test(testo);
}

/*
 Restituisce un riferimento da appendere a un elemento che contiene le caselle da sorvegliare
 (o alla casella stessa). Da quel momento ogni inserimento che non sia di sole cifre, dentro un
 discendente marcato con `data-solo-cifre`, viene annullato prima di toccare il valore.

 L'ascoltatore e' registrato a mano e non come `onBeforeInput` di React: la prop sintetica di
 React 18 e' un residuo storico che non corrisponde all'evento nativo e non si annulla in modo
 affidabile.
*/
export function useSoloCifre<T extends HTMLElement>() {
  const riferimento = useRef<T>(null);

  useEffect(() => {
    const radice = riferimento.current;
    if (!radice) {
      return undefined;
    }
    const ascoltatore = (evento: Event) => {
      const bersaglio = evento.target as HTMLElement | null;
      if (!bersaglio?.hasAttribute?.(SOLO_CIFRE_ATTRIBUTO)) {
        return;
      }
      if (inserimentoDaRifiutare(evento as InputEvent)) {
        evento.preventDefault();
      }
    };
    radice.addEventListener("beforeinput", ascoltatore);
    return () => radice.removeEventListener("beforeinput", ascoltatore);
  }, []);

  return riferimento;
}
